import { jsx as _jsx } from "react/jsx-runtime";
import { Box } from "@mui/material";
import { CalendarDate, getLocalTimeZone, today as todayCD } from "@internationalized/date";
/* ================= DATE HELPERS ================= */
/** "2026-08-01" (or a full ISO string) → CalendarDate. Falls back to today. */
export const isoToCalendarDate = (iso) => {
    if (!iso)
        return todayCD(getLocalTimeZone());
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d)
        return todayCD(getLocalTimeZone());
    return new CalendarDate(y, m, d);
};
/** CalendarDate → "YYYY-MM-DD" (what the API is expected to take). */
export const calendarDateToIso = (date) => `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
/** "2026-08-01" → "08/01/2026", matching the reference listing. */
export const fmtDate = (iso) => {
    if (!iso)
        return "—";
    const [y, m, d] = iso.slice(0, 10).split("-");
    if (!y || !m || !d)
        return "—";
    return `${m}/${d}/${y}`;
};
/** "2026-08-01" → "Aug 01, 2026" — used where a longer form reads better. */
export const fmtDateLong = (iso) => {
    if (!iso)
        return "—";
    const date = new Date(`${iso.slice(0, 10)}T00:00:00`);
    if (isNaN(date.getTime()))
        return "—";
    return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};
export const fmtDateRange = (start, end) => `${fmtDate(start)} - ${fmtDate(end)}`;
/** "monday" → "Monday"; "" → "" */
const titleCase = (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
/** "09:00" → "9:00 AM" */
export const fmtTime = (time) => {
    const [h, m] = time.split(":").map(Number);
    if (isNaN(h) || isNaN(m))
        return time;
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};
/** Human sentence for a schedule, e.g. "Weekly on Monday at 8:30 AM". */
export const fmtSchedule = (schedule) => {
    if (!schedule)
        return "Not scheduled";
    const time = fmtTime(schedule.time);
    if (schedule.frequency === "daily")
        return `Daily at ${time}`;
    if (schedule.frequency === "weekly") {
        return `Weekly on ${titleCase(schedule.day)} at ${time}`;
    }
    return `Monthly on day ${schedule.day} at ${time}`;
};
/* ================= STATUS PILL ================= */
const STATUS_STYLES = {
    draft: { label: "Draft", bg: "#F2F4F7", color: "#475467" },
    ready: { label: "Ready", bg: "#EFF8FF", color: "#175CD3" },
    sent: { label: "Sent", bg: "#ECFDF3", color: "#027A48" },
    scheduled: { label: "Scheduled", bg: "#FEF6EE", color: "#B93815" },
};
export const StatusPill = ({ status }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.draft;
    return (_jsx(Box, { component: "span", sx: {
            display: "inline-flex",
            alignItems: "center",
            px: 0.75,
            py: "1px",
            borderRadius: 999,
            bgcolor: style.bg,
            color: style.color,
            fontSize: "11px",
            fontWeight: 600,
            lineHeight: "16px",
            whiteSpace: "nowrap",
        }, children: style.label }));
};
